/* oxlint-disable no-param-reassign */

'use client';

import cx from 'classnames';
import { ChangeEventHandler, InputHTMLAttributes, Ref, useState } from 'react';

import CircleCheckIcon from '../assets/svgs/circle-check.svg';
import VisionOffIcon from '../assets/svgs/vision-off.svg';
import VisionOnIcon from '../assets/svgs/vision-on.svg';
import styles from './sessionInput.module.scss';

interface SessionInputProps extends InputHTMLAttributes<HTMLInputElement> {
  ref?: Ref<HTMLInputElement>;
  className?: string;
  withValidIcon?: boolean;
  isValid?: boolean;
  isError?: boolean;
}

export const SessionInput = ({
  ref,
  className,
  withValidIcon,
  isValid,
  isError,
  onChange,
  ...inputProps
}: SessionInputProps) => {
  const customOnChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    e.currentTarget.value = e.currentTarget.value.trim();
    onChange?.(e);
  };

  return (
    <div
      className={cx(styles.wrapper, styles.large, className, {
        [styles.withIcon]: withValidIcon,
        [styles.isError]: isError,
      })}
    >
      <input type='text' onChange={customOnChange} ref={ref} className={styles.sessionInput} {...inputProps} />
      {withValidIcon && <CircleCheckIcon className={cx(styles.checkIcon, { [styles.isValid]: isValid })} />}
    </div>
  );
};

interface PasswordProps extends SessionInputProps {
  onChangeType?: (inputType: 'password' | 'text') => void;
}

export const PasswordInput = ({ ref, className, isError, onChange, onChangeType, ...inputProps }: PasswordProps) => {
  const [inputType, setInputType] = useState<'password' | 'text'>('password');

  const onClickTogglePassword = () => {
    const nextType = inputType === 'password' ? 'text' : 'password';

    setInputType(nextType);
    onChangeType?.(nextType);
  };

  const customOnChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    e.currentTarget.value = e.currentTarget.value.trim();
    onChange?.(e);
  };

  const isVisiblePassword = inputType !== 'password';

  return (
    <div className={cx(styles.wrapper, styles.large, styles.withIcon, className, { [styles.isError]: isError })}>
      <input type={inputType} onChange={customOnChange} ref={ref} {...inputProps} />
      <button
        type='button'
        tabIndex={-1}
        title={isVisiblePassword ? 'Hide password' : 'Show password'}
        onClick={onClickTogglePassword}
        className={styles.passwordButton}
      >
        {isVisiblePassword ? <VisionOnIcon /> : <VisionOffIcon />}
      </button>
    </div>
  );
};
